"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AIAssistContextHelper = void 0;
const assistant_component_types_1 = require("../types/assistant.component.types");
class AIAssistContextHelper {
    /**
     * Build context from chat messages and assistant entries
     * @param messages Current chat messages
     * @param entries Assistant entries
     * @param options Context building options
     * @returns Context data object
     */
    static buildContext(htmlStripService, messages, entries, options = {}) {
        const { maxMessages = 50, maxLength = 8000, includeSystemMessages = false, includeAssistantMessages = true, includeUserMessages = true, stripHtml = true } = options;
        // Filter messages based on options
        let filteredMessages = messages.filter(msg => {
            if (msg.role === 'system' && !includeSystemMessages)
                return false;
            if (msg.role === 'assistant' && !includeAssistantMessages)
                return false;
            if (msg.role === 'user' && !includeUserMessages)
                return false;
            return true;
        });
        // Limit number of messages
        if (filteredMessages.length > maxMessages) {
            filteredMessages = filteredMessages.slice(-maxMessages);
        }
        // Clean messages if HTML stripping is enabled
        const cleanedMessages = stripHtml
            ? this.stripHtmlFromMessages(filteredMessages, htmlStripService)
            : filteredMessages;
        // Calculate total length and truncate if necessary
        let totalLength = 0;
        const contextMessages = [];
        for (let i = cleanedMessages.length - 1; i >= 0; i--) {
            const msg = cleanedMessages[i];
            const msgLength = (msg.content || '').length;
            if (totalLength + msgLength > maxLength && contextMessages.length > 0) {
                break;
            }
            contextMessages.unshift(msg);
            totalLength += msgLength;
        }
        // Generate context summary
        const contextSummary = this.generateContextSummary(contextMessages, entries);
        return {
            messages: filteredMessages,
            entries,
            cleanedMessages: contextMessages,
            contextSummary,
            messageCount: contextMessages.length,
            totalLength
        };
    }
    /**
     * Strip HTML from chat messages using the HTML strip service
     * @param messages Array of chat messages
     * @returns Messages with HTML stripped
     */
    static stripHtmlFromMessages(messages, htmlStripService) {
        return messages.map(msg => ({
            ...msg,
            content: typeof msg.content === 'string'
                ? htmlStripService.sanitizeAssistantMessage(msg.content)
                : msg.content
        }));
    }
    /**
     * Clean assistant message content
     * @param content Message content
     * @returns Cleaned content
     */
    static cleanAssistantContent(content, htmlStripService) {
        return htmlStripService.sanitizeAssistantMessage(content);
    }
    /**
     * Generate a summary of the current context
     * @param messages Context messages
     * @param entries Assistant entries
     * @returns Context summary string
     */
    static generateContextSummary(messages, entries) {
        const userMessages = messages.filter(m => m.role === 'user').length;
        const assistantMessages = messages.filter(m => m.role === 'assistant').length;
        const activeEntries = entries.filter(e => e.type === assistant_component_types_1.AssistantEntryType.ACTION || e.type === assistant_component_types_1.AssistantEntryType.MESSAGE).length;
        const parts = [];
        if (userMessages > 0) {
            parts.push(`${userMessages} ${'context.userMessages'.t()}`);
        }
        if (assistantMessages > 0) {
            parts.push(`${assistantMessages} ${'context.assistantMessages'.t()}`);
        }
        if (activeEntries > 0) {
            parts.push(`${activeEntries} ${'context.activeEntries'.t()}`);
        }
        return parts.length > 0
            ? 'context.summary'.t() + ': ' + parts.join(', ')
            : 'context.empty'.t();
    }
    /**
     * Extract relevant context for specific entry type
     * @param messages Chat messages
     * @param entryType Type of assistant entry
     * @param maxLength Maximum context length
     * @returns Relevant context string
     */
    static extractRelevantContext(messages, entryType, htmlStripService, maxLength = 2000) {
        // Filter and prioritize messages based on entry type
        let relevantMessages = messages;
        switch (entryType) {
            case assistant_component_types_1.AssistantEntryType.ACTION:
                // For actions, focus on recent user messages
                relevantMessages = messages.filter(m => m.role === 'user').slice(-3);
                break;
            case assistant_component_types_1.AssistantEntryType.MESSAGE:
                // For messages, include both user and assistant messages
                relevantMessages = messages.slice(-5);
                break;
            case assistant_component_types_1.AssistantEntryType.STATUS:
                // For status, include broader context
                relevantMessages = messages.slice(-10);
                break;
            default:
                relevantMessages = messages.slice(-5);
        }
        // Strip HTML and build context string
        const cleanedMessages = this.stripHtmlFromMessages(relevantMessages, htmlStripService);
        let context = '';
        for (const msg of cleanedMessages) {
            const msgText = `[${msg.role}]: ${msg.content || ''}\n`;
            if (context.length + msgText.length > maxLength) {
                break;
            }
            context += msgText;
        }
        return context.trim();
    }
    /**
     * Prepare context for API calls
     * @param messages Chat messages
     * @param options Context options
     * @returns Prepared context object
     */
    static prepareApiContext(messages, htmlStripService, options = {}) {
        const contextData = this.buildContext(htmlStripService, messages, [], {
            ...options,
            stripHtml: true
        });
        const context = contextData.cleanedMessages
            .map(msg => `[${msg.role}]: ${msg.content || ''}`)
            .join('\n');
        const hasHtml = messages.some(msg => typeof msg.content === 'string' && htmlStripService.containsHtml(msg.content));
        return {
            context,
            messageCount: contextData.messageCount,
            hasHtml
        };
    }
    /**
     * Validate and clean context data
     * @param contextData Raw context data
     * @returns Validated and cleaned context
     */
    static validateContext(contextData, htmlStripService) {
        if (!contextData || typeof contextData !== 'object') {
            return null;
        }
        try {
            const validated = {
                messages: Array.isArray(contextData.messages) ? contextData.messages : [],
                entries: Array.isArray(contextData.entries) ? contextData.entries : [],
                cleanedMessages: Array.isArray(contextData.cleanedMessages) ? contextData.cleanedMessages : [],
                contextSummary: typeof contextData.contextSummary === 'string' ? contextData.contextSummary : '',
                messageCount: typeof contextData.messageCount === 'number' ? contextData.messageCount : 0,
                totalLength: typeof contextData.totalLength === 'number' ? contextData.totalLength : 0
            };
            // Strip HTML from all message content
            validated.messages = this.stripHtmlFromMessages(validated.messages, htmlStripService);
            validated.cleanedMessages = this.stripHtmlFromMessages(validated.cleanedMessages, htmlStripService);
            return validated;
        }
        catch (error) {
            console.error('context.validationError'.t(), error);
            return null;
        }
    }
    /**
     * Merge multiple context sources
     * @param contexts Array of context data objects
     * @param options Merging options
     * @returns Merged context data
     */
    static mergeContexts(contexts, htmlStripService, options = {}) {
        const { maxMessages = 50, maxLength = 8000 } = options;
        const allMessages = [];
        const allEntries = [];
        contexts.forEach(ctx => {
            allMessages.push(...ctx.messages);
            allEntries.push(...ctx.entries);
        });
        // Remove duplicates and merge
        const uniqueMessages = this.removeDuplicateMessages(allMessages);
        const uniqueEntries = this.removeDuplicateEntries(allEntries);
        return this.buildContext(htmlStripService, uniqueMessages, uniqueEntries, {
            maxMessages,
            maxLength,
            stripHtml: true
        });
    }
    /**
     * Remove duplicate messages based on content and timestamp
     * @param messages Array of messages
     * @returns Deduplicated messages
     */
    static removeDuplicateMessages(messages) {
        const seen = new Set();
        return messages.filter(msg => {
            const key = `${msg.role}:${msg.content}:${msg.timestamp || ''}`;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }
    /**
     * Remove duplicate entries based on text content and type
     * @param entries Array of entries
     * @returns Deduplicated entries
     */
    static removeDuplicateEntries(entries) {
        const seen = new Set();
        return entries.filter(entry => {
            const key = `${entry.type}:${entry.text || ''}`;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }
}
exports.AIAssistContextHelper = AIAssistContextHelper;
